import { useMemo, useRef, useState } from "react";

import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";

import GameFields from "../shared/GameFields";
import resolveGameConfig from "../shared/resolveGameConfig";

import {
  calculateProgress,
  calculateAccuracy,
  calculateAverageReactionTime,
  calculateTotalTime,
  calculateErrorCount,
} from "../shared/gameMetrics";

import "./SentenceCompletion.css";

const SENTENCE_BANK = [
  {
    id: "wash_hands",
    category: "Vida diaria",
    sentence: "Para lavarme las manos necesito",
    answer: "jabón",
    distractors: ["prisa", "arena", "papel"],
  },
  {
    id: "drink_water",
    category: "Vida diaria",
    sentence: "Cuando tengo sed bebo",
    answer: "agua",
    distractors: ["pan", "jabón", "arena"],
  },
  {
    id: "cut_bread",
    category: "Funciones de objetos",
    sentence: "Para cortar el pan utilizo un",
    answer: "cuchillo",
    distractors: ["plato", "vaso", "cojín"],
  },
  {
    id: "eat_soup",
    category: "Funciones de objetos",
    sentence: "Para comer sopa utilizo una",
    answer: "cuchara",
    distractors: ["raqueta", "almohada", "regla"],
  },
  {
    id: "listen_radio",
    category: "Medios de comunicación",
    sentence: "Para escuchar las noticias puedo encender la",
    answer: "radio",
    distractors: ["ventana", "cuchara", "mochila"],
  },
  {
    id: "watch_tv",
    category: "Medios de comunicación",
    sentence: "Para ver una película en casa enciendo la",
    answer: "televisión",
    distractors: ["radio", "nevera", "lavadora"],
  },
  {
    id: "play_tennis",
    category: "Deportes",
    sentence: "Para jugar al tenis necesito una",
    answer: "raqueta",
    distractors: ["portería", "cuchara", "maleta"],
  },
  {
    id: "football_goal",
    category: "Deportes",
    sentence: "En el fútbol intentamos meter la pelota en la",
    answer: "portería",
    distractors: ["raqueta", "piscina", "ventana"],
  },
  {
    id: "healthy_food",
    category: "Salud",
    sentence: "Comer frutas y verduras ayuda a cuidar",
    answer: "la salud",
    distractors: ["el avión", "la carretera", "la mochila"],
  },
  {
    id: "buy_meat",
    category: "Lugares",
    sentence: "Para comprar carne voy a la",
    answer: "carnicería",
    distractors: ["frutería", "biblioteca", "farmacia"],
  },
  {
    id: "buy_fruit",
    category: "Lugares",
    sentence: "Para comprar fruta voy a la",
    answer: "frutería",
    distractors: ["carnicería", "ferretería", "zapatería"],
  },
  {
    id: "see_eyes",
    category: "Cuerpo humano",
    sentence: "Para ver utilizamos los",
    answer: "ojos",
    distractors: ["pies", "brazos", "codos"],
  },
  {
    id: "walk_feet",
    category: "Cuerpo humano",
    sentence: "Para caminar utilizamos los",
    answer: "pies",
    distractors: ["ojos", "dedos", "hombros"],
  },
  {
    id: "watch_movie",
    category: "Lugares",
    sentence: "Para ver una película en una pantalla grande voy al",
    answer: "cine",
    distractors: ["parque", "hospital", "mercado"],
  },
  {
    id: "see_play",
    category: "Lugares",
    sentence: "Para ver una obra con actores puedo ir al",
    answer: "teatro",
    distractors: ["supermercado", "gimnasio", "aeropuerto"],
  },
  {
    id: "play_parchis",
    category: "Juegos",
    sentence: "El parchís es un juego de",
    answer: "mesa",
    distractors: ["fútbol", "natación", "bicicleta"],
  },
  {
    id: "cards",
    category: "Juegos",
    sentence: "Para jugar a la baraja utilizamos",
    answer: "cartas",
    distractors: ["pelotas", "tenedores", "zapatos"],
  },
  {
    id: "mothers_mother",
    category: "Familia",
    sentence: "La madre de mi madre es mi",
    answer: "abuela",
    distractors: ["prima", "tía", "hermana"],
  },
  {
    id: "aunts_daughter",
    category: "Familia",
    sentence: "La hija de mi tía es mi",
    answer: "prima",
    distractors: ["abuela", "madre", "sobrina"],
  },
  {
    id: "blue_sky",
    category: "Descripción",
    sentence: "En un día despejado el cielo suele ser",
    answer: "azul",
    distractors: ["cuadrado", "ruidoso", "blando"],
  },
  {
    id: "beautiful_flowers",
    category: "Descripción",
    sentence: "Las flores del jardín son muy",
    answer: "bonitas",
    distractors: ["rápidos", "saladas", "redondos"],
  },
  {
    id: "rain_umbrella",
    category: "Causa y efecto",
    sentence: "Cuando llueve utilizo un",
    answer: "paraguas",
    distractors: ["anillo", "peine", "tenedor"],
  },
  {
    id: "cold_coat",
    category: "Causa y efecto",
    sentence: "Cuando hace mucho frío me pongo un",
    answer: "abrigo",
    distractors: ["bañador", "plato", "cuaderno"],
  },
  {
    id: "night_sleep",
    category: "Rutinas",
    sentence: "Por la noche voy a la cama para",
    answer: "dormir",
    distractors: ["saltar", "cocinar", "correr"],
  },
  {
    id: "hungry_eat",
    category: "Rutinas",
    sentence: "Cuando tengo hambre necesito",
    answer: "comer",
    distractors: ["volar", "pintar", "nadar"],
  },
  {
    id: "plane_fly",
    category: "Transportes",
    sentence: "Para viajar por el aire puedo utilizar un",
    answer: "avión",
    distractors: ["coche", "barco", "patinete"],
  },
  {
    id: "bike_pedals",
    category: "Transportes",
    sentence: "Para mover una bicicleta tengo que usar los",
    answer: "pedales",
    distractors: ["cubiertos", "botones", "cajones"],
  },
  {
    id: "greeting_hello",
    category: "Comunicación",
    sentence: "Cuando llego a un lugar puedo decir",
    answer: "hola",
    distractors: ["adiós", "duerme", "corre"],
  },
  {
    id: "farewell_goodbye",
    category: "Comunicación",
    sentence: "Cuando me marcho puedo decir",
    answer: "adiós",
    distractors: ["hola", "gracias", "entra"],
  },
  {
    id: "thanks",
    category: "Comunicación",
    sentence: "Cuando alguien me ayuda puedo decir",
    answer: "gracias",
    distractors: ["silencio", "corre", "duerme"],
  },
  {
    id: "apology",
    category: "Comunicación",
    sentence: "Si hago daño a alguien por accidente debo pedir",
    answer: "perdón",
    distractors: ["comida", "una pelota", "una ventana"],
  },
  {
    id: "dog_barks",
    category: "Animales",
    sentence: "El perro hace",
    answer: "guau",
    distractors: ["miau", "pío", "muuu"],
  },
  {
    id: "cat_meows",
    category: "Animales",
    sentence: "El gato hace",
    answer: "miau",
    distractors: ["guau", "muuu", "quiquiriquí"],
  },
  {
    id: "fish_water",
    category: "Animales",
    sentence: "Los peces viven en el",
    answer: "agua",
    distractors: ["desierto", "tejado", "armario"],
  },
  {
    id: "bird_fly",
    category: "Animales",
    sentence: "La mayoría de los pájaros pueden",
    answer: "volar",
    distractors: ["conducir", "escribir", "cocinar"],
  },
  {
    id: "teacher_school",
    category: "Profesiones",
    sentence: "La maestra trabaja en el",
    answer: "colegio",
    distractors: ["aeropuerto", "bosque", "estadio"],
  },
  {
    id: "doctor_hospital",
    category: "Profesiones",
    sentence: "El médico puede trabajar en un",
    answer: "hospital",
    distractors: ["cine", "parque", "restaurante"],
  },
  {
    id: "firefighter_fire",
    category: "Profesiones",
    sentence: "El bombero ayuda a apagar",
    answer: "incendios",
    distractors: ["cuadernos", "canciones", "ventanas"],
  },
  {
    id: "cook_food",
    category: "Profesiones",
    sentence: "El cocinero prepara",
    answer: "comida",
    distractors: ["carreteras", "zapatos", "cartas"],
  },
  {
    id: "sun_day",
    category: "Temporalidad",
    sentence: "El sol suele verse durante el",
    answer: "día",
    distractors: ["invierno", "sueño", "silencio"],
  },
  {
    id: "moon_night",
    category: "Temporalidad",
    sentence: "La luna suele verse mejor por la",
    answer: "noche",
    distractors: ["mesa", "mañana", "cocina"],
  },
  {
    id: "breakfast_morning",
    category: "Temporalidad",
    sentence: "Normalmente desayunamos por la",
    answer: "mañana",
    distractors: ["medianoche", "puerta", "playa"],
  },
  {
    id: "summer_hot",
    category: "Temporalidad",
    sentence: "En verano suele hacer",
    answer: "calor",
    distractors: ["hambre", "ruido", "sueño"],
  },
  {
    id: "ice_cold",
    category: "Propiedades",
    sentence: "El hielo está",
    answer: "frío",
    distractors: ["caliente", "seco", "ruidoso"],
  },
  {
    id: "fire_hot",
    category: "Propiedades",
    sentence: "El fuego está",
    answer: "caliente",
    distractors: ["congelado", "mojado", "blando"],
  },
  {
    id: "sugar_sweet",
    category: "Propiedades",
    sentence: "El azúcar tiene un sabor",
    answer: "dulce",
    distractors: ["salado", "ruidoso", "cuadrado"],
  },
  {
    id: "lemon_sour",
    category: "Propiedades",
    sentence: "El limón suele tener un sabor",
    answer: "ácido",
    distractors: ["dulce", "silencioso", "blando"],
  },
  {
    id: "open_key",
    category: "Funciones de objetos",
    sentence: "Para abrir una puerta cerrada puedo necesitar una",
    answer: "llave",
    distractors: ["servilleta", "cuchara", "pelota"],
  },
  {
    id: "write_pencil",
    category: "Funciones de objetos",
    sentence: "Para escribir en un cuaderno puedo usar un",
    answer: "lápiz",
    distractors: ["tenedor", "cojín", "zapato"],
  },
  {
    id: "brush_teeth",
    category: "Funciones de objetos",
    sentence: "Para limpiarme los dientes utilizo un",
    answer: "cepillo de dientes",
    distractors: ["martillo", "peine", "cuchillo"],
  },
  {
    id: "dry_towel",
    category: "Funciones de objetos",
    sentence: "Después de ducharme me seco con una",
    answer: "toalla",
    distractors: ["sartén", "raqueta", "regla"],
  },
  {
    id: "sad_cry",
    category: "Emociones",
    sentence: "Cuando una persona está muy triste puede",
    answer: "llorar",
    distractors: ["volar", "ladrar", "derretirse"],
  },
  {
    id: "happy_smile",
    category: "Emociones",
    sentence: "Cuando estamos contentos solemos",
    answer: "sonreír",
    distractors: ["escondernos", "temblar", "dormir"],
  },
  {
    id: "afraid_safety",
    category: "Emociones",
    sentence: "Cuando tengo miedo puedo buscar",
    answer: "ayuda",
    distractors: ["una cuchara", "un semáforo", "una galleta"],
  },
  {
    id: "tired_rest",
    category: "Estados",
    sentence: "Si estoy muy cansado necesito",
    answer: "descansar",
    distractors: ["saltar", "gritar", "correr"],
  },
  {
    id: "wet_floor",
    category: "Inferencias",
    sentence: "Si el suelo está mojado debo caminar con",
    answer: "cuidado",
    distractors: ["hambre", "prisa", "música"],
  },
  {
    id: "dark_light",
    category: "Inferencias",
    sentence: "Si una habitación está oscura puedo encender la",
    answer: "luz",
    distractors: ["ducha", "puerta", "radio"],
  },
  {
    id: "lost_ask",
    category: "Inferencias",
    sentence: "Si no sé dónde estoy puedo pedir",
    answer: "ayuda",
    distractors: ["un postre", "un dibujo", "una carrera"],
  },
  {
    id: "rain_ground",
    category: "Inferencias",
    sentence: "Si ha llovido es probable que el suelo esté",
    answer: "mojado",
    distractors: ["dormido", "vacío", "dulce"],
  },
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 3,
    autoPlayAudio: true,
    showCategory: true,
  },

  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 4,
    autoPlayAudio: true,
    showCategory: false,
  },

  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 5,
    autoPlayAudio: false,
    showCategory: false,
  },
};

const shuffleArray = (items) => {
  const shuffled = [...items];

  for (
    let index = shuffled.length - 1;
    index > 0;
    index -= 1
  ) {
    const randomIndex = Math.floor(
      Math.random() * (index + 1)
    );

    [shuffled[index], shuffled[randomIndex]] = [
      shuffled[randomIndex],
      shuffled[index],
    ];
  }

  return shuffled;
};

const createRound = ({
  itemCount,
  previousId = null,
}) => {
  const safeItemCount = Math.min(
    Math.max(Number(itemCount) || 3, 2),
    5
  );

  const availableSentences =
    SENTENCE_BANK.filter(
      (sentence) => sentence.id !== previousId
    );

  const pool =
    availableSentences.length > 0
      ? availableSentences
      : SENTENCE_BANK;

  const item =
    pool[
      Math.floor(Math.random() * pool.length)
    ];

  const distractorCount = safeItemCount - 1;

  const ownDistractors = shuffleArray(
    item.distractors
  ).slice(0, distractorCount);

  const additionalDistractors =
    SENTENCE_BANK.flatMap((sentence) => [
      sentence.answer,
      ...sentence.distractors,
    ]).filter(
      (word) =>
        word !== item.answer &&
        !ownDistractors.includes(word)
    );

  const distractors = [
    ...ownDistractors,
    ...shuffleArray(additionalDistractors),
  ].slice(0, distractorCount);

  const options = shuffleArray([
    {
      id: `${item.id}-correct`,
      text: item.answer,
      isCorrect: true,
    },

    ...distractors.map((text, index) => ({
      id: `${item.id}-distractor-${index}`,
      text,
      isCorrect: false,
    })),
  ]);

  return {
    id: item.id,
    category: item.category,
    sentence: item.sentence,
    answer: item.answer,
    options,
  };
};

function SentenceCompletion() {
  const [selectedDifficulty, setSelectedDifficulty] =
    useState(null);

  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);

  const [started, setStarted] = useState(false);
  const [finished, setFinished] = useState(false);

  const [correctAnswers, setCorrectAnswers] =
    useState(0);

  const [errors, setErrors] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const [roundStartTime, setRoundStartTime] =
    useState(null);

  const [responseLocked, setResponseLocked] =
    useState(false);

  const [selectedOptionId, setSelectedOptionId] =
    useState(null);

  const [audioSupported, setAudioSupported] =
    useState(
      typeof window !== "undefined" &&
        "speechSynthesis" in window
    );

  const speechTimeoutRef = useRef(null);

  const totalRounds = config?.totalRounds || 0;

  const progress = useMemo(
    () => calculateProgress(roundIndex, totalRounds),
    [roundIndex, totalRounds]
  );

  const totalAnswers = correctAnswers + errors;

  const accuracy = calculateAccuracy(
    correctAnswers,
    totalAnswers
  );

  const totalErrors =
    calculateErrorCount(errors);

  const averageReactionTime =
    calculateAverageReactionTime(responses);

  const totalTime = calculateTotalTime(
    startTime,
    endTime
  );

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        errors: totalErrors,
        averageReactionTime,
        difficulty: selectedDifficulty,
      })
    : null;

  const stopSpeech = () => {
    if (
      typeof window !== "undefined" &&
      "speechSynthesis" in window
    ) {
      window.speechSynthesis.cancel();
    }

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }
  };

  const speakSentence = (sentenceToSpeak) => {
    if (
      typeof window === "undefined" ||
      !("speechSynthesis" in window) ||
      !sentenceToSpeak
    ) {
      setAudioSupported(false);
      return;
    }

    stopSpeech();

    const utterance = new SpeechSynthesisUtterance(
      `${sentenceToSpeak}...`
    );

    utterance.lang = "es-ES";
    utterance.rate = 0.82;
    utterance.pitch = 1;
    utterance.volume = 1;

    window.speechSynthesis.speak(utterance);
  };

  const scheduleAutomaticAudio = (
    sentenceToSpeak,
    shouldPlay
  ) => {
    if (!shouldPlay) {
      return;
    }

    speechTimeoutRef.current = setTimeout(() => {
      speakSentence(sentenceToSpeak);
    }, 450);
  };

  const startGame = (
    difficultyOrConfig =
      selectedDifficulty || "easy"
  ) => {
    stopSpeech();

    const {
      difficultyKey,
      config: resolvedConfig,
    } = resolveGameConfig(
      difficultyOrConfig,
      GAME_CONFIG
    );

    const nextConfig = {
      ...resolvedConfig,
      difficultyKey,

      totalRounds:
        Number(resolvedConfig.totalRounds) || 8,

      itemCount: Math.min(
        Math.max(
          Number(resolvedConfig.itemCount) || 3,
          2
        ),
        5
      ),

      autoPlayAudio:
        resolvedConfig.autoPlayAudio !== false,

      showCategory:
        resolvedConfig.showCategory !== false,
    };

    const firstRound = createRound(nextConfig);
    const now = Date.now();

    setSelectedDifficulty(difficultyKey);
    setConfig(nextConfig);

    setRoundIndex(0);
    setRound(firstRound);

    setStarted(true);
    setFinished(false);

    setCorrectAnswers(0);
    setErrors(0);
    setResponses([]);

    setStartTime(now);
    setEndTime(null);
    setRoundStartTime(now);

    setResponseLocked(false);
    setSelectedOptionId(null);

    scheduleAutomaticAudio(
      firstRound.sentence,
      nextConfig.autoPlayAudio
    );
  };

  const finishGame = () => {
    stopSpeech();

    setEndTime(Date.now());
    setStarted(false);
    setFinished(true);
  };

  const moveToNextRound = () => {
    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= totalRounds) {
      finishGame();
      return;
    }

    const nextRound = createRound({
      ...config,
      previousId: round.id,
    });

    setRoundIndex(nextRoundIndex);
    setRound(nextRound);

    setRoundStartTime(Date.now());

    setResponseLocked(false);
    setSelectedOptionId(null);

    scheduleAutomaticAudio(
      nextRound.sentence,
      config.autoPlayAudio
    );
  };

  const handleAnswer = (option) => {
    if (!round || responseLocked) {
      return;
    }

    stopSpeech();

    setResponseLocked(true);
    setSelectedOptionId(option.id);

    const responseTime =
      Date.now() - roundStartTime;

    setResponses((current) => [
      ...current,
      responseTime,
    ]);

    if (option.isCorrect) {
      setCorrectAnswers(
        (current) => current + 1
      );
    } else {
      setErrors((current) => current + 1);
    }

    setTimeout(() => {
      moveToNextRound();
    }, 650);
  };

  if (!started && !finished) {
    return (
      <GameLauncher
        title="Completar frases"
        description="Escucha o lee una frase incompleta y selecciona la palabra que permite terminarla correctamente."
        areas={[
          "Comprensión verbal",
          "Cierre gramatical",
          "Vocabulario",
          "Razonamiento verbal",
        ]}
        defaultDifficulty="easy"
        customFields={[
          GameFields.totalRounds({
            min: 4,
            max: 30,
            defaultValue: 10,
          }),

          GameFields.itemCount({
            label: "Opciones de respuesta",
            min: 2,
            max: 5,
            defaultValue: 3,
          }),
        ]}
        onStart={startGame}
      />
    );
  }

  if (finished) {
    return (
      <div className="sentence-completion-page">
        <div className="sentence-completion-results">
          <GameResults
            title="Resultados"
            correctAnswers={correctAnswers}
            totalAnswers={totalAnswers}
            accuracy={accuracy}
            errors={totalErrors}
            averageReactionTime={
              averageReactionTime
            }
            totalTime={totalTime}
            onRestart={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          />

          <div className="sentence-completion-results-grid">
            <div className="sentence-completion-result-card">
              <span>Aciertos</span>
              <strong>{correctAnswers}</strong>
            </div>

            <div className="sentence-completion-result-card">
              <span>Errores</span>
              <strong>{errors}</strong>
            </div>

            <div className="sentence-completion-result-card">
              <span>Precisión</span>
              <strong>
                {Math.round(accuracy)}%
              </strong>
            </div>

            <div className="sentence-completion-result-card">
              <span>Frases completadas</span>
              <strong>{totalAnswers}</strong>
            </div>
          </div>

          <GameInterpretation
            interpretation={interpretation}
          />

          <button
            type="button"
            className="sentence-completion-restart"
            onClick={() =>
              startGame(
                selectedDifficulty || "easy"
              )
            }
          >
            Jugar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sentence-completion-page">
      <main className="sentence-completion-card">
        <header className="sentence-completion-header">
          <div>
            <p className="sentence-completion-area">
              Comprensión verbal
            </p>

            <h1>Completar frases</h1>

            <p>
              Escucha o lee la frase y elige la
              opción que permite completarla.
            </p>
          </div>

          <div className="sentence-completion-score">
            <span>Aciertos</span>
            <strong>{correctAnswers}</strong>
          </div>
        </header>

        <GameProgress
          currentRound={roundIndex + 1}
          totalRounds={totalRounds}
          progress={progress}
        />

        <section className="sentence-completion-game">
          {config?.showCategory && (
            <span className="sentence-completion-category">
              {round?.category}
            </span>
          )}

          <button
            type="button"
            className="sentence-completion-audio"
            onClick={() =>
              speakSentence(round?.sentence)
            }
            disabled={!audioSupported}
          >
            <span
              className="sentence-completion-audio-icon"
              aria-hidden="true"
            >
              🔊
            </span>

            <span>
              {audioSupported
                ? "Escuchar la frase"
                : "Audio no disponible"}
            </span>
          </button>

          <div className="sentence-completion-sentence">
            <span>{round?.sentence}</span>
            <strong>...</strong>
          </div>

          <p className="sentence-completion-question">
            ¿Cómo termina la frase?
          </p>

          <div
            className="sentence-completion-options"
            style={{
              gridTemplateColumns: `repeat(${Math.min(
                round?.options.length || 2,
                3
              )}, minmax(150px, 1fr))`,
            }}
          >
            {round?.options.map((option) => {
              const isSelected =
                selectedOptionId === option.id;

              const optionClassName = [
                "sentence-completion-option",

                isSelected &&
                  option.isCorrect &&
                  "sentence-completion-option-correct",

                isSelected &&
                  !option.isCorrect &&
                  "sentence-completion-option-error",
              ]
                .filter(Boolean)
                .join(" ");

              return (
                <button
                  type="button"
                  key={option.id}
                  className={optionClassName}
                  onClick={() =>
                    handleAnswer(option)
                  }
                  disabled={responseLocked}
                >
                  {option.text}
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SentenceCompletion;